// ---------------------------------------------------------------------------
// priorityTier.js — Material Processing Priority (workflow support only).
//
// Priority Tier is a SORT/FILTER signal, NOT a release/authority signal.
// It does not bypass Asset Pathway, DEMO_ONLY, clearance, or lifecycle
// gating. These predicates and constants are shared by the tracks list,
// intake workspaces list, and their editor pages so the sort and badge
// rendering stay consistent.
// ---------------------------------------------------------------------------

export const PRIORITY_TIER = {
  URGENT: "URGENT",
  P1: "P1",
  P2: "P2",
  P3: "P3",
  P4: "P4",
};

// Sort order — URGENT first, unassigned last.
export const PRIORITY_TIER_SORT_ORDER = ["URGENT", "P1", "P2", "P3", "P4"];

export const PRIORITY_TIER_LABELS = {
  URGENT: "URGENT — Compliance / Legal Risk Override",
  P1: "P1 — Contracted Client / Onboarding",
  P2: "P2 — Paid Client",
  P3: "P3 — Clean Kingswork Release / Sync Candidate",
  P4: "P4 — Accepted Development Demo",
  "": "Unassigned",
};

// Reason categories surfaced in the ReasonModal. When the operator picks
// URGENT the modal uses PRIORITY_URGENT_CATEGORIES; otherwise the general
// PRIORITY_TIER_CATEGORIES.
export const PRIORITY_URGENT_CATEGORIES = [
  "Takedown threat",
  "Ownership dispute",
  "Release already live with errors",
  "Attorney / legal deadline",
  "Payment dispute",
  "Regulatory / compliance risk",
  "Other documented urgent reason",
];

export const PRIORITY_TIER_CATEGORIES = [
  "New Contracted Client",
  "New Paid Client",
  "Kingswork Release / Sync Candidate Advancement",
  "Development Demo Acceptance",
  "Client Priority Adjustment",
  "Operational Sequencing Correction",
  "Compliance Review Outcome",
  "Other",
];

/** Return the canonical string form; empty means unassigned. */
export function getPriorityTier(record) {
  if (!record) return "";
  const t = (record.priority_tier || "").toString().trim().toUpperCase();
  return t;
}

/** Sort key: URGENT=0, P1=1, P2=2, P3=3, P4=4, unassigned=5. */
export function priorityTierSortKey(record) {
  const t = getPriorityTier(record);
  const idx = PRIORITY_TIER_SORT_ORDER.indexOf(t);
  return idx < 0 ? PRIORITY_TIER_SORT_ORDER.length : idx;
}

/**
 * Stable, in-place-safe sort helper. Returns a NEW array sorted by
 * priority first (URGENT → P4 → unassigned), then by ``dateField``
 * oldest-first within tier. Used as the default queue order.
 */
export function sortByPriorityThenDate(records, dateField = "created_at") {
  const arr = Array.isArray(records) ? [...records] : [];
  arr.sort((a, b) => {
    const pa = priorityTierSortKey(a);
    const pb = priorityTierSortKey(b);
    if (pa !== pb) return pa - pb;
    const da = String(a?.[dateField] || "");
    const db_ = String(b?.[dateField] || "");
    if (da === db_) return 0;
    return da < db_ ? -1 : 1;
  });
  return arr;
}

/**
 * Badge style variants. Kept accessible (WCAG-friendly foreground/border
 * contrast). URGENT is deliberately loud; the P-tiers use restrained
 * palette so the URGENT badge always stands out in a mixed list.
 */
export function priorityBadgeVariant(tier) {
  const t = (tier || "").toString().trim().toUpperCase();
  switch (t) {
    case "URGENT":
      return {
        label: "URGENT",
        title: "URGENT — Compliance / Legal Risk Override",
        className: "bg-red-100 text-red-900 border-red-400 font-semibold",
      };
    case "P1":
      return {
        label: "P1",
        title: PRIORITY_TIER_LABELS.P1,
        className: "bg-[hsl(347_72%_22%)]/10 text-[hsl(347_72%_22%)] border-[hsl(347_72%_22%)]/40 font-medium",
      };
    case "P2":
      return {
        label: "P2",
        title: PRIORITY_TIER_LABELS.P2,
        className: "bg-blue-50 text-blue-900 border-blue-300 font-medium",
      };
    case "P3":
      return {
        label: "P3",
        title: PRIORITY_TIER_LABELS.P3,
        className: "bg-amber-50 text-amber-900 border-amber-300",
      };
    case "P4":
      return {
        label: "P4",
        title: PRIORITY_TIER_LABELS.P4,
        className: "bg-slate-100 text-slate-800 border-slate-300",
      };
    default:
      return {
        label: "—",
        title: "Unassigned",
        className: "bg-transparent text-muted-foreground border-transparent",
      };
  }
}
